/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// notification-stack.js — a minimal top-right toast stack (the sibling
// projects' notification pattern, trimmed for Chip Hippo). The simulation
// surfaces short / conflict / oscillation / magic-smoke messages here. Each
// toast auto-dismisses; a `key` de-dupes so a re-settle doesn't pile up
// duplicates of the same standing warning.

import { el } from "../dom.js";
import { t } from "../i18n.js";

const DEFAULT_TIMEOUT_MS = 6000;

/** The most toasts shown at once; the older ones wait behind a "+N more"
    line (click it to see them all), so a run that goes badly wrong cannot
    bury the desk's right-hand side in a column of warnings. */
export const MAX_SHOWN_TOASTS = 4;

export class NotificationStack {
  #el;
  #live = new Map(); // key → { toast, timer }, oldest first
  #more; // the "+N more" line
  #expanded = false; // the more line was clicked: every toast is shown

  /** @param {HTMLElement} container - typically document.body / the app root. */
  constructor(container) {
    this.#el = el("div", {
      class: "toast-stack",
      role: "status",
      "aria-live": "polite",
    });
    this.#more = el("button", {
      class: "toast-more",
      type: "button",
      hidden: true,
      onClick: () => {
        this.#expanded = true;
        this.#layout();
      },
    });
    this.#el.append(this.#more);
    container.append(this.#el);
  }

  /** Show the newest MAX_SHOWN_TOASTS (all of them once expanded) and say
      how many more wait behind them. */
  #layout() {
    const entries = [...this.#live.values()];
    const over = this.#expanded ? 0 : Math.max(0, entries.length - MAX_SHOWN_TOASTS); // prettier-ignore
    entries.forEach((e, i) => e.toast.classList.toggle("toast--overflow", i < over)); // prettier-ignore
    this.#more.hidden = over === 0;
    if (over) this.#more.textContent = t("toast.more", { count: over });
    if (entries.length <= MAX_SHOWN_TOASTS) this.#expanded = false;
  }

  /**
   * Show a toast. `key` collapses repeats (the same standing warning refreshes
   * its timer instead of stacking). `variant` styles it
   * (info | warning | danger). `sticky` toasts don't auto-dismiss.
   *
   * `actionLabel` + `onAction` add ONE button to the toast — the shape a toast
   * needs when it is offering something rather than just saying something
   * ("Update ready" → Restart). The whole toast is a dismiss target, so the
   * button stops the click from propagating into it: exactly the discipline
   * the toolbar's pill readouts follow, for the same reason — a control nested
   * inside a bigger one must not also fire what it sits in. The toast dismisses
   * itself afterwards, since the offer has been answered either way.
   *
   * Re-notifying a LIVE key updates the toast's words in place rather than
   * stacking a second one. That is what a standing warning wanted all along
   * (nothing changes when the text is the same), and it is what a job reporting
   * progress needs — replacing the node instead would rebuild its action button
   * under the pointer that is reaching for it.
   *
   * `dismissible: false` takes away the click-anywhere-to-close, for the one
   * shape that needs it: a toast that is a running job's ONLY interface, where
   * a stray click would throw away the Cancel button and leave the job running
   * with no way to stop it. Such a toast is the caller's to dismiss.
   *
   * @param {{ key?: string, variant?: string, title?: string, message: string,
   *           sticky?: boolean, dismissible?: boolean, actionLabel?: string,
   *           onAction?: () => void }} opts
   */
  notify({
    key,
    variant = "info",
    title,
    message,
    sticky = false,
    dismissible = true,
    actionLabel,
    onAction,
  } = {}) {
    const id = key ?? `${variant}:${message}`;
    const existing = this.#live.get(id);
    if (existing) {
      clearTimeout(existing.timer);
      // A standing warning can grow worse (a brownout that becomes brown
      // smoke): the colour follows the words.
      if (existing.variant !== variant) {
        existing.toast.classList.remove(`toast--${existing.variant}`);
        existing.toast.classList.add(`toast--${variant}`);
        existing.variant = variant;
      }
      const titleEl = existing.toast.querySelector(".toast-title");
      if (titleEl && title != null) titleEl.textContent = title;
      const messageEl = existing.toast.querySelector(".toast-message");
      if (messageEl) messageEl.textContent = message;
      if (!sticky) existing.timer = this.#arm(id);
      return;
    }
    const action =
      actionLabel &&
      el("button", {
        class: "toast-action",
        type: "button",
        text: actionLabel,
        onClick: (e) => {
          e.stopPropagation();
          this.dismiss(id);
          onAction?.();
        },
      });
    const toast = el(
      "div",
      { class: `toast toast--${variant}`, dataset: { key: id } },
      [
        title && el("div", { class: "toast-title", text: title }),
        el("div", { class: "toast-message", text: message }),
        action,
      ].filter(Boolean),
    );
    if (dismissible) toast.addEventListener("click", () => this.dismiss(id));
    this.#el.insertBefore(toast, this.#more);
    this.#live.set(id, {
      toast,
      variant,
      timer: sticky ? null : this.#arm(id),
    });
    this.#layout();
  }

  #arm(id) {
    return setTimeout(() => this.dismiss(id), DEFAULT_TIMEOUT_MS);
  }

  dismiss(id) {
    const entry = this.#live.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    entry.toast.remove();
    this.#live.delete(id);
    this.#layout();
  }
}
